import React, { useContext, useEffect, useState } from "react";
import { Alert, Button, Card, Dialog, Dropdown, Notification, Progress, Skeleton, toast, Tooltip } from "components/ui";
import ParticleBackground from 'react-particle-backgrounds'
import { particleBackgroundSettings } from 'configs/particle-background.config';
import { formatNumber } from "utils/formatNumber";
import { CompanyContext } from "contexts/CompanyContext";
import { SessionContext } from "contexts/SessionContext";
import { Link, useNavigate } from "react-router-dom";
import { HiOutlineChevronDown, HiOutlineCreditCard, HiOutlineSparkles } from "react-icons/hi";
import { HiCog8Tooth, HiArrowUpRight, HiOutlineDocumentText, HiOutlineUser, HiPlus } from "react-icons/hi2";
import { IoBusinessOutline } from "react-icons/io5";
import AddOn from "./AddOn";
import { PriorityModalContext } from "contexts/PriorityModalContext";
import OutOfWordsModal from "components/layout/OutOfWordsModal";
import OutOfUsersModal from "components/layout/OutOfUsersModal";
import OutOfCompaniesModal from "components/layout/OutOfCompaniesModal";
import CancelSubscriptionModal from "./CancelSubscriptionModal";
import moment from "moment";
import { api as Api } from "api/http";
import { Trans } from "react-i18next";
import Subscription from ".";
import OutOfImagesModal from "components/layout/OutOfImagesModal";
import { TbCode, TbDatabase, TbPalette, TbPhotoAi, TbStack2, TbTextCaption } from "react-icons/tb";
import { AppSumoContext } from "contexts/AppSumoContext";
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';
import AppSumoUpgradeButton from "components/layout/AppSumoUpgradeButton";
import ProductTierChips from "components/layout/ProductTierChips";
import {
    APPSUMO_MAX_TIER,
    APPSUMO_MAX_LICENSE_TIER,
    isAppSumoCodesCampaignLive,
    isAppSumoStudioCampaignLive,
} from "components/ui/utils/constant";
import ApiKeysModal from "components/shared/ApiKeysModal";
import WhitelabelModal from "components/shared/WhitelabelModal";

const MySubscription = () => {
    
    const { isSumoling, isPlus, getTier, isLicensee, getLicenseTier } = useContext(AppSumoContext);
    const { company, mainUser } = useContext(CompanyContext);
    const { user, updateUserData, planType } = useContext(SessionContext);
    const { openModalPriority } = useContext(PriorityModalContext);
    const [ isPriceDetailsDialogOpen, setIsPriceDetailsDialogOpen ] = useState(false);
    const [ showCancelSubscriptionModal, setShowCancelSubscriptionModal ] = useState(false);
    const [ isLoadingResumeSubscription, setIsLoadingResumeSubscription ] = useState(false);
    const [ totalMonthly, setTotalMonthly ] = useState(0);
    const [ isLoadingTotalMonthly, setIsLoadingTotalMonthly ] = useState(false)
    const [ upcomingInvoice, setUpcomingInvoice ] = useState();
    const navigate = useNavigate();
    const { whitelabel, isWhitelabel } = useWhitelabel();
    const isWhitelabelNonOwner = isWhitelabel && mainUser?.id !== user?.id;
    const plan = user?.subscription?.items?.filter(e => e.product.type == "plan")?.[0];
    const recurring = user?.subscription?.items?.[0]?.price?.recurring;

    // Enhanced progress bar component with modern design
    const EnhancedProgressBar = ({ 
        spent, 
        allowed, 
        label, 
        textLabel,
        icon,
        color = "primary",
        onAddMore = null,
        suffix = "",
        decimals = 0
    }) => {
        const isInfinite = allowed === null || allowed === 0;
        const progressPercent = isInfinite ? 0 : (spent / allowed) * 100;
        
        const colorClasses = {
            primary: {
                bg: "bg-gradient-to-r from-blue-500/10 to-indigo-500/10",
                border: "border-blue-200/30 dark:border-blue-700/30",
                text: "text-blue-700 dark:text-blue-300",
                progress: "from-blue-400 to-indigo-500",
                button: "bg-blue-500 hover:bg-blue-600",
                glow: "shadow-blue-500/20"
            },
            secondary: {
                bg: "bg-gradient-to-r from-emerald-500/10 to-teal-500/10",
                border: "border-emerald-200/30 dark:border-emerald-700/30",
                text: "text-emerald-700 dark:text-emerald-300",
                progress: "from-emerald-400 to-teal-500",
                button: "bg-emerald-500 hover:bg-emerald-600",
                glow: "shadow-emerald-500/20"
            },
            purple: {
                bg: "bg-gradient-to-r from-purple-500/10 to-violet-500/10",
                border: "border-purple-200/30 dark:border-purple-700/30",
                text: "text-purple-700 dark:text-purple-300",
                progress: "from-purple-400 to-violet-500",
                button: "bg-purple-500 hover:bg-purple-600",
                glow: "shadow-purple-500/20"
            },
            orange: {
                bg: "bg-gradient-to-r from-orange-500/10 to-amber-500/10",
                border: "border-orange-200/30 dark:border-orange-700/30",
                text: "text-orange-700 dark:text-orange-300",
                progress: "from-orange-400 to-amber-500",
                button: "bg-orange-500 hover:bg-orange-600",
                glow: "shadow-orange-500/20"
            },
            blue: {
                bg: "bg-gradient-to-r from-blue-500/10 to-indigo-500/10",
                border: "border-blue-200/30 dark:border-blue-700/30",
                text: "text-blue-700 dark:text-blue-300",
                progress: "from-blue-400 to-indigo-500",
                button: "bg-blue-500 hover:bg-blue-600",
                glow: "shadow-blue-500/20"
            }
        };

        const currentColor = colorClasses[color];
        
        return (
            <div className={`relative p-4 rounded-2xl border ${currentColor.bg} ${currentColor.border} backdrop-blur-sm transition-all duration-300 hover:shadow-xl ${currentColor.glow}`}>
                <div className='flex items-center justify-between mb-3'>
                    <div className='flex items-center gap-3'>
                        <div className={`p-2 rounded-xl bg-gradient-to-br ${currentColor.progress} shadow-lg`}>
                            {icon}
                        </div>
                        <span className={`text-sm font-bold ${currentColor.text}`}>{label}</span>
                    </div>
                    {onAddMore && (
                        <button
                            onClick={onAddMore}
                            className={`w-8 h-8 ${currentColor.button} rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-lg`}
                            title={`Add more ${textLabel}`}
                        >
                            <HiPlus className="w-3.5 h-3.5 text-white" />
                        </button>
                    )}
                </div>
                
                <div className="space-y-3">
                    <div className='flex items-center justify-between'>
                        <span className='text-xs text-gray-600 dark:text-gray-400 font-medium'>Usage</span>
                        <span className='text-sm font-bold text-gray-800 dark:text-gray-200'>
                            {formatNumber(spent, decimals)} 
                            <span className="text-gray-400 dark:text-gray-500 font-normal text-xs"> / {isInfinite ? '∞' : formatNumber(allowed, decimals)} {suffix}</span>
                        </span>
                    </div>
                    
                    <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        {isInfinite ? (
                            <div 
                                className={`absolute top-0 left-0 h-full bg-gradient-to-r ${currentColor.progress} rounded-full transition-all duration-500 ease-out shadow-sm animate-pulse`}
                                style={{ width: '100%' }}
                            />
                        ) : (
                            <div 
                                className={`absolute top-0 left-0 h-full bg-gradient-to-r ${currentColor.progress} rounded-full transition-all duration-500 ease-out shadow-sm`}
                                style={{ width: `${Math.min(progressPercent, 100)}%` }}
                            />
                        )}
                    </div>

                    <div className='flex justify-between items-center text-xs text-gray-500 dark:text-gray-400'>
                        <span>{isInfinite ? 'Unlimited' : `${formatNumber(allowed - spent, decimals)} ${suffix}`} remaining</span>
                        <span>{isInfinite ? '∞' : `${Math.round(progressPercent)}%`} used</span>
                    </div>
                </div>
            </div>
        );
    };

    const start = user?.subscription?.trial_ends_at ? new Date(user?.subscription?.trial_ends_at) : new Date(user?.subscription?.created_at);
    let end = start;
    if (recurring == "year") {
        end.setFullYear(new Date().getFullYear());
        if (end < new Date()) {
            end = end.getTime() + 1000 * 3600 * 24 * 365;
        }
    } else {
        end.setFullYear(new Date().getFullYear());
        end.setMonth(new Date().getMonth());
        if (end < new Date()) {
            end = end.getTime() + 1000 * 3600 * 24 * 365 / 12;
        }
    }

    const resumeSubscription = () => {
        setIsLoadingResumeSubscription(true);
        Api.put(`/subscriptions`, { resume: 1 }).then((response) => {
            updateUserData();
            toast.push(<Notification type="success" title="Your subscription has been resumed!" />);
        }).catch((err) => {
            toast.push(<Notification type="danger" title={err?.message} />);
        }).finally(() => {
            setIsLoadingResumeSubscription(false);
        });
    }

    useEffect(() => {
        if (company?.id && (!company || (company?.user?.id || company?.user_id) != user?.id)) {
            toast.push(<Notification title="You don't have access to this section on this company." type="warning" />)
            navigate(`/home`);
        } 
    }, [company?.role?.name]);

    useEffect(() => {
        setIsLoadingTotalMonthly(true);
        Api.get(`/invoices/upcoming`, {}).then(({ data: invoice }) => {
            setUpcomingInvoice(invoice);
            setTotalMonthly(invoice?.total / 100 / (recurring == "year" ? 12 : 1));
        }).catch((err) => {

        }).finally(() => {
            setIsLoadingTotalMonthly(false);
        });
    }, [JSON.stringify(user?.subscription || {})]);

    return (
        <div className='flex w-full h-full gap-6 p-4 lg:p-6'>
            <Card className="flex-1 shrink-0 w-full overflow-hidden !m-0 h-full shadow-2xl" bodyClass="flex flex-col lg:flex-row !p-0 h-full">
                {/* Enhanced Header Section */}
                <div className='p-6 lg:p-8 lg:py-12 px-6 lg:px-12 bg-gradient-to-br from-primary-600 to-primary-700 !opacity-[1] flex flex-col justify-between gap-4 overflow-hidden relative w-full lg:max-w-[30%]'>
                    <ParticleBackground settings={particleBackgroundSettings} className="absolute pointer-events-none -m-10" />
                    
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
                    
                    <div className="relative z-10">
                        {(!!isLicensee() && getTier() > 0)
                            ? <ProductTierChips tone="onGradient" layout="pills" className="mb-1" />
                            : <h1 className='mega-title text-white text-4xl lg:text-5xl font-extrabold leading-tight'>
                                {!!isLicensee()
                                    ? <>Tier {getLicenseTier()}</>
                                    : !!isSumoling()
                                        ? <>Tier {getTier()}</>
                                        : <>
                                            <Trans i18nKey="subscriptionSideInfo.plan">Plan</Trans>&nbsp;
                                            {plan?.product?.label}
                                        </>
                                }
                            </h1>
                        }
                        <p className="text-white/90 mt-0 lg:mt-3 text-lg font-medium">
                            {(!!isSumoling() || !!isLicensee())
                                ? <Trans i18nKey="appSumo.lifetimeDeal">AppSumo Lifetime Deal</Trans>
                                : plan?.product?.description
                            }
                        </p>
                    </div>
                    
                    {(!!isSumoling() || !!isLicensee())
                        ? <div className="hidden lg:flex items-center justify-start lg:justify-center gap-4 relative z-10">
                            <div className="p-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
                                <img src={`/img/others/appsumo.webp`} className="w-[40px] lg:w-[50px] object-contain drop-shadow-lg" />
                            </div>
                            <img src={`/img/others/appsumo-text.png`} className="invert brightness-[0] w-[125px] lg:w-[150px] object-contain drop-shadow-lg" />
                        </div>
                        : <div className="hidden relative z-10 lg:flex justify-center">
                            <div className="p-6 rounded-3xl bg-white/10 backdrop-blur-sm border border-white/20">
                                <img src={`/img/others/plan-${plan?.product?.name}.png`} className="w-[125px] lg:w-[150px] object-contain drop-shadow-lg" />
                            </div>
                        </div>
                    }
                </div>
                <div className='w-full flex-1 overflow-y-auto max-h-full'>
                    <div className='p-6 lg:p-10 flex flex-col gap-8 w-full'>
                        {user?.subscription?.stripe_status == 'past_due' && 
                            <Alert className="w-full" type="danger" title="Payment error." showIcon customIcon={<HiOutlineCreditCard />}>
                                <Trans i18nKey={`subscription.cardError`}>We couldn't charge your card. Please, check your card details and try again.</Trans> {!!user?.subscription?.ends_at && `You won't be able to access your account after ${moment(new Date(user?.subscription?.ends_at)).format("MMMM DD, YYYY")}.`}
                                <br />
                                <br />
                                <Link to={`/settings/billing`}><b><Trans i18nKey={`subscription.goToBilling`}>Go to Billing</Trans></b></Link>
                            </Alert>
                        }
                        {/* Enhanced Pricing Display */}
                        <div className='flex flex-col lg:flex-row gap-8 w-full items-start lg:items-center justify-between'>
                            {(isSumoling() || isLicensee())
                                ? <div className="flex-1 w-full">
                                    <div className="p-6 rounded-2xl border shadow-lg w-full"
                                        style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.06) 0%, rgba(245,158,11,0.04) 100%)', borderColor: 'rgba(251,191,36,0.2)' }}>
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="p-2 rounded-lg shadow-sm" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                                                <HiOutlineSparkles className="w-4 h-4 text-white" />
                                            </div>
                                            <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">Lifetime Deal</span>
                                        </div>

                                        <div className="space-y-3">
                                            <h1 className='text-3xl lg:text-4xl font-extrabold text-gray-800 dark:text-gray-200'>
                                                Lifetime
                                                <span className='text-lg font-normal text-gray-500 ml-2'>— paid once, yours forever</span>
                                            </h1>

                                            <div className="flex flex-wrap items-center gap-2">
                                                <ProductTierChips tone="onLight" layout="pills" />
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                                                    Active
                                                </span>
                                            </div>

                                            {((isAppSumoStudioCampaignLive() && isLicensee() && getLicenseTier() < APPSUMO_MAX_LICENSE_TIER) ||
                                              (isAppSumoCodesCampaignLive() && getTier() > 0 && getTier() < APPSUMO_MAX_TIER)) && (
                                                <div className="pt-3 mt-2 border-t border-amber-200/40 dark:border-amber-700/30 flex flex-col sm:flex-row gap-2">
                                                    {isAppSumoStudioCampaignLive() && isLicensee() && getLicenseTier() < APPSUMO_MAX_LICENSE_TIER && (
                                                        <div className="flex-1 min-w-[200px]">
                                                            <AppSumoUpgradeButton variant="studio" />
                                                        </div>
                                                    )}
                                                    {isAppSumoCodesCampaignLive() && getTier() > 0 && getTier() < APPSUMO_MAX_TIER && (
                                                        <div className="flex-1 min-w-[200px]">
                                                            <AppSumoUpgradeButton variant="codes" />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                : !isLoadingTotalMonthly
                                    ? <div className="flex-1 w-full">
                                        <div className="p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50/50 dark:from-slate-800 dark:to-slate-900 border border-slate-200/50 dark:border-slate-700/50 shadow-lg w-full">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="p-2 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 shadow-sm">
                                                    <HiOutlineCreditCard className="w-4 h-4 text-white" />
                                                </div>
                                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Current Billing</span>
                                            </div>

                                            <div className="space-y-2">
                                                <h1 className='flex items-baseline gap-2 text-4xl lg:text-5xl font-extrabold text-gray-800 dark:text-gray-200'>
                                                    <span className='text-xl text-gray-500'>$</span>
                                                    {formatNumber(totalMonthly, 2)}
                                                    <span className='text-lg font-normal text-gray-500'>/mo</span>
                                                </h1>

                                                <div className="flex flex-col gap-1">
                                                    <span className='text-sm text-gray-600 dark:text-gray-400'>
                                                        <Trans i18nKey={`subscription.billed`}>Billed</Trans> {recurring == "year" ? `Annually` : `Monthly`}
                                                        {recurring == "year" && (
                                                            <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 font-medium">
                                                                Save ${formatNumber((totalMonthly * 12 * 0.2), 2)}/year
                                                            </span>
                                                        )}
                                                    </span>
                                                    <button
                                                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors text-left font-medium flex items-center gap-1"
                                                        onClick={() => setIsPriceDetailsDialogOpen(true)}
                                                    >
                                                        <Trans i18nKey={`subscription.viewPriceDetails`}>View price details</Trans>
                                                        <HiArrowUpRight className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    : <div className="flex-1">
                                        <div className="p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50/50 dark:from-slate-800 dark:to-slate-900 border border-slate-200/50 dark:border-slate-700/50">
                                            <div className="flex flex-col gap-3">
                                                <Skeleton variant="rectangle" className="w-full !h-[45px]" />
                                                <Skeleton variant="rectangle" className="w-3/4 !h-[14px]" />
                                                <Skeleton variant="rectangle" className="w-1/2 !h-[14px]" />
                                            </div>
                                        </div>
                                    </div>
                            }

                            {/* Action Buttons */}
                            <div className="flex flex-col justify-center items-center lg:items-end gap-3 w-full lg:w-auto">
                                {(!isSumoling() && !isLicensee())
                                    ? !user?.subscription?.ends_at
                                        ? <div className="flex justify-center flex-col items-center gap-2">
                                            <Button
                                                variant="solid"
                                                className="!px-6 !py-3 !rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                                                onClick={() => openModalPriority(<Subscription isModal={true} />, 1200, true, true, `!p-0 !bg-tertiary-100 dark:!bg-gray-900 zoom-95`)}
                                                icon={<HiCog8Tooth />}
                                            >
                                                Change Plan
                                            </Button>
                                            <button
                                                className="text-xs text-gray-500 hover:text-gray-600 transition-colors font-medium"
                                                onClick={() => setShowCancelSubscriptionModal(true)}
                                            >
                                                Cancel subscription
                                            </button>
                                        </div>
                                        : <div className="flex flex-col items-end gap-3">
                                            <div className="flex gap-3 items-center">
                                                <Button
                                                    variant="twoTone"
                                                    className="!px-4 !py-2 !rounded-xl"
                                                    onClick={() => openModalPriority(<Subscription isModal={true} />, 1200, true, true, `!p-0 !bg-tertiary-100 dark:!bg-gray-900 zoom-95`)}
                                                    icon={<HiCog8Tooth />}
                                                >
                                                    <Trans i18nKey={`subscription.changePlan`}>Change Plan</Trans>
                                                </Button>
                                                <Button
                                                    variant="solid"
                                                    className="!px-6 !py-3 !rounded-xl shadow-lg"
                                                    loading={isLoadingResumeSubscription}
                                                    disabled={isLoadingResumeSubscription}
                                                    onClick={() => resumeSubscription()}
                                                    icon={<HiOutlineSparkles />}
                                                >
                                                    <Trans i18nKey={`subscription.resumeSubscription`}>Resume Subscription</Trans>
                                                </Button>
                                            </div>
                                            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                                                <span className="text-xs text-amber-700 dark:text-amber-300 font-medium">
                                                <Trans i18nKey={`subscription.yourSubscriptionHasBeenCancelled`}>Your subscription has been cancelled.</Trans>
                                            </span>
                                            </div>
                                        </div>
                                    : null 
                                    /* <div className="w-full hidden lg:w-auto p-4 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                            <span className="text-sm font-medium text-green-700 dark:text-green-300">Lifetime Access</span>
                                        </div>
                                    </div> */
                                }
                            </div>
                        </div>
                    </div>
                    {/* Stacking info for users with both license + tokens */}
                    {!!isLicensee() && getTier() > 0 && !isWhitelabelNonOwner && (
                        <div className="mx-8 lg:mx-12 mt-0 mb-6 p-4 rounded-xl border flex items-start gap-3"
                            style={{ background: 'rgba(251, 191, 36, 0.05)', borderColor: 'rgba(251, 191, 36, 0.2)' }}>
                            <div className="p-1.5 rounded-lg shrink-0 mt-0.5" style={{ background: 'rgba(251, 191, 36, 0.12)' }}>
                                <TbStack2 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-0.5">Your limits stack</p>
                                <p className="text-xs text-amber-700/70 dark:text-amber-400/70 leading-relaxed">
                                    You have both an AppSumo license (Tier {getLicenseTier()}) and {getTier()} redeemed code{getTier() > 1 ? 's' : ''}. All limits from both are combined into a single balance.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Enhanced Usage Section */}
                    <div className="bg-gradient-to-br from-slate-50/50 to-blue-50/30 dark:from-slate-800/50 dark:to-slate-900/30 border-t border-gray-200/50 dark:border-gray-700/50">
                        <div className='p-8 lg:p-12'>
                            <div className="flex flex-col lg:flex-row items-center justify-between mb-8">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-sm">
                                        <HiOutlineSparkles className="w-5 h-5 text-white" />
                                    </div>
                                    <h2 className="!text-xl lg:!text-2xl font-bold text-gray-800 dark:text-gray-200">Usage Overview</h2>
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Resets: {new Date(user?.balance?.words_renews_at).toLocaleDateString()}
                                </span>
                            </div>

                            <div className="flex flex-col lg:grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {planType !== 'ai_studio' && (
                                    <EnhancedProgressBar
                                        spent={user?.balance?.words_spent}
                                        allowed={user?.balance?.words_allowed}
                                        label={<Trans i18nKey="subscription.words">AI Text Credits</Trans>}
                                        textLabel="AI Text Credits"
                                        color="secondary"
                                        icon={<TbTextCaption className="w-4 h-4 text-white" />}
                                        onAddMore={() => openModalPriority(<OutOfWordsModal limit={false} />)}
                                    />
                                )}

                                {!!(user?.balance?.images_allowed || user?.balance?.images_bonus_total) && planType !== 'ai_studio' && (
                                    <EnhancedProgressBar
                                        spent={user?.balance?.images_spent}
                                        allowed={user?.balance?.images_allowed}
                                        label={<Trans i18nKey="subscription.aiImages">AI Media Credits</Trans>}
                                        textLabel="AI Media Credits"
                                        color="primary"
                                        icon={<TbPhotoAi className="w-4 h-4 text-white" />}
                                        onAddMore={() => openModalPriority(<OutOfImagesModal limit={false} />)}
                                    />
                                )}
                                {!!(user?.balance?.images_allowed || user?.balance?.images_bonus_total) && planType === 'ai_studio' && (
                                    <div className="col-span-2">
                                        <EnhancedProgressBar
                                            spent={user?.balance?.images_spent}
                                            allowed={user?.balance?.images_allowed}
                                            label={<Trans i18nKey="subscription.aiImages">AI Media Credits</Trans>}
                                            textLabel="AI Media Credits"
                                            color="primary"
                                            icon={<TbPhotoAi className="w-4 h-4 text-white" />}
                                            onAddMore={() => openModalPriority(<OutOfImagesModal limit={false} />)}
                                        />
                                    </div>
                                )}

                                {!!user?.balance?.images_bonus_credits?.length && (
                                    <div className="col-span-2 p-4 rounded-2xl border border-amber-200/30 dark:border-amber-700/30 bg-gradient-to-r from-amber-500/5 to-orange-500/5">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="p-1.5 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 shadow-sm">
                                                <HiOutlineSparkles className="w-4 h-4 text-white" />
                                            </div>
                                            <span className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                                                {isLicensee() ? 'Add-on Media Credits' : 'Bonus Media Credits'}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                                                {formatNumber(user?.balance?.images_bonus_total)} total remaining
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {user?.balance?.images_bonus_credits?.map((credit) => (
                                                <div key={credit.id} className="p-3 rounded-xl bg-white/60 dark:bg-gray-800/60 border border-amber-200/20 dark:border-amber-700/20">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
                                                            {formatNumber(credit.remaining)}
                                                        </span>
                                                        <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                                            / {formatNumber(credit.quantity)}
                                                        </span>
                                                    </div>
                                                    {credit.note && (
                                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 truncate">{credit.note}</p>
                                                    )}
                                                    {credit.expires_at ? (
                                                        <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                                                            Expires: {new Date(credit.expires_at).toLocaleDateString()}
                                                        </p>
                                                    ) : (
                                                        <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1">
                                                            No expiry
                                                        </p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <EnhancedProgressBar
                                    spent={user?.balance?.users_spent}
                                    allowed={user?.balance?.users_allowed}
                                    label={<Trans i18nKey="subscription.users">Team Members</Trans>}
                                    textLabel="Users"
                                    color="purple"
                                    icon={<HiOutlineUser className="w-4 h-4 text-white" />}
                                    onAddMore={() => openModalPriority(<OutOfUsersModal limit={false} />)}
                                />

                                <EnhancedProgressBar
                                    spent={user?.balance?.companies_spent}
                                    allowed={user?.balance?.companies_allowed}
                                    label={<Trans i18nKey="subscription.companies">Companies</Trans>}
                                    textLabel="Companies"
                                    color="orange"
                                    icon={<IoBusinessOutline className="w-4 h-4 text-white" />}
                                    onAddMore={() => openModalPriority(<OutOfCompaniesModal limit={false} />)}
                                />

                                {planType !== 'ai_studio' && (
                                    <div className="col-span-2">
                                        <EnhancedProgressBar
                                            spent={user?.balance?.bytes_spent ? (parseInt(user.balance.bytes_spent, 10) / (1024 ** 3)) : 0}
                                            allowed={user?.balance?.bytes_allowed ? (parseInt(user.balance.bytes_allowed, 10) / (1024 ** 3)) : 0}
                                            label={<Trans i18nKey="subscription.storage">Storage (GB)</Trans>}
                                            textLabel="Storage (GB)"
                                            color="blue"
                                            icon={<TbDatabase className="w-4 h-4 text-white" />}
                                            suffix="GB"
                                            decimals={2}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Enhanced Add-ons Section */}
                    <div className="bg-gradient-to-br from-white to-slate-50/50 dark:from-gray-900 dark:to-slate-900/50 border-t border-gray-200/50 dark:border-gray-700/50">
                        <div className='p-8 lg:p-12'>
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
                                <div className="flex items-center gap-4">
                                    <div className="p-2 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-sm">
                                        <HiPlus className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h2 className='!text-xl lg:!text-2xl font-bold text-gray-800 dark:text-gray-200'>
                                            <Trans i18nKey={`subscription.myAddOns`}>My Add-ons</Trans>
                                        </h2>
                                    </div>
                                </div>
                                
                                <Dropdown
                                    placement="top-end"
                                    menuClass="max-h-[400px] overflow-y-auto !rounded-2xl !shadow-2xl !border-0"
                                    renderTitle={
                                        <Button className="flex items-center justify-between gap-3 !px-6 !py-3 !rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700" variant="solid">
                                            <HiPlus className="w-4 h-4" />
                                            <span><Trans i18nKey={`subscription.newAddOns`}>Add More</Trans></span>
                                            <HiOutlineChevronDown className="w-4 h-4" />
                                        </Button>
                                    }
                                >
                                    <Dropdown.Item className="!py-3 !px-4 hover:!bg-blue-50 dark:hover:!bg-blue-900/20" onClick={() => openModalPriority(<OutOfImagesModal limit={false} />)} eventKey="new_item">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-blue-500 text-white">
                                                <TbPhotoAi className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <span className="font-medium"><Trans i18nKey={`subscription.addImages`}>AI Media Credits</Trans></span>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Generate images and videos</p>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Item className="!py-3 !px-4 hover:!bg-emerald-50 dark:hover:!bg-emerald-900/20" onClick={() => openModalPriority(<OutOfWordsModal limit={false} />)} eventKey="new_item">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-emerald-500 text-white">
                                                <TbTextCaption className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <span className="font-medium"><Trans i18nKey={`subscription.addWords`}>AI Text Credits</Trans></span>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Generate content and captions</p>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Item className="!py-3 !px-4 hover:!bg-purple-50 dark:hover:!bg-purple-900/20"  onClick={() => openModalPriority(<OutOfUsersModal limit={false} />)}eventKey="new_item">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-purple-500 text-white">
                                                <HiOutlineUser className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <span className="font-medium"><Trans i18nKey={`subscription.addUsers`}>Team Members</Trans></span>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Invite more team members</p>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Item className="!py-3 !px-4 hover:!bg-orange-50 dark:hover:!bg-orange-900/20" onClick={() => openModalPriority(<OutOfCompaniesModal limit={false} />)} eventKey="new_item">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-orange-500 text-white">
                                                <IoBusinessOutline className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <span className="font-medium"><Trans i18nKey={`subscription.addCompanies`}>Companies</Trans></span>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Manage multiple companies</p>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Item className="!py-3 !px-4 hover:!bg-gray-50 dark:hover:!bg-gray-800" onClick={() => openModalPriority(<ApiKeysModal />)} eventKey="new_item">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-gray-600 text-white">
                                                <TbCode className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <span className="font-medium"><Trans i18nKey={`subscription.apiAccess`}>API Access</Trans></span>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Integrate with your apps</p>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Item className="!py-3 !px-4 hover:!bg-pink-50 dark:hover:!bg-pink-900/20" onClick={() => openModalPriority(<WhitelabelModal />)} eventKey="new_item">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-pink-500 text-white">
                                                <TbPalette className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <span className="font-medium"><Trans i18nKey={`subscription.whiteLabel`}>White Label</Trans></span>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Custom branding options</p>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                </Dropdown>
                            </div>

                            {!(user?.subscription?.items?.filter(item => item.product.type == "add-on")?.length > 0) && (
                                <div className="w-full rounded-3xl border-2 border-dashed border-gray-300 dark:border-gray-600 p-12 flex flex-col lg:flex-row items-center justify-center gap-8 bg-gradient-to-br from-gray-50/50 to-blue-50/30 dark:from-gray-800/50 dark:to-gray-900/50 hover:shadow-lg transition-all duration-300">
                                    <div className="flex items-center justify-center text-center gap-6">
                                        {/* <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                                            <TbPhotoAi className="text-3xl text-white" />
                                        </div> */}
                                        <div>
                                            <h4 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-2">No add-ons yet</h4>
                                            <p className="text-gray-600 dark:text-gray-400 max-w-md">Supercharge your plan with additional credits, team members, and premium features.</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {user?.subscription?.items?.filter(item => item.product.type == "add-on")?.length > 0 && (
                                <div className="grid grid-cols-1 md:grid-cols-2 xxl:grid-cols-3 gap-6">
                                    {user?.subscription?.items?.filter(item => item.product.type == "add-on").map((item) => (
                                        <div key={item?.price?.id} className="transform hover:scale-105 transition-all duration-200">
                                            <AddOn recurring={recurring} item={item} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Card>

            {/* Enhanced Price Details Dialog */}
            <Dialog
                isOpen={isPriceDetailsDialogOpen}
                onClose={() => setIsPriceDetailsDialogOpen(false)}
                onRequestClose={() => setIsPriceDetailsDialogOpen(false)}
                overlayClassName="flex items-center justify-center w-full"
                className="!rounded-3xl !shadow-2xl !border-0 overflow-hidden"
            >
                <div className="p-0">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="p-2 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 shadow-sm">
                            <HiOutlineCreditCard className="w-5 h-5 text-white" />
                        </div>
                        <h4 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
                            <Trans i18nKey={`subscription.priceDetails`}>Price Breakdown</Trans>
                        </h4>
                    </div>
                    
                    <div className="space-y-4 mb-8">
                        {upcomingInvoice?.lines?.data?.map((item, key) => (
                            <div key={key} className="flex justify-between items-center p-4 rounded-2xl bg-gradient-to-r from-slate-50 to-blue-50/50 dark:from-slate-800 dark:to-slate-900 border border-slate-200/50 dark:border-slate-700/50">
                                <div className="flex-1">
                                    <span className="font-medium text-gray-800 dark:text-gray-200">{item?.description}</span>
                                </div>
                                <div className="flex flex-col items-end">
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-sm text-gray-500">$</span>
                                        <span className="text-xl font-bold text-gray-800 dark:text-gray-200">
                                            {formatNumber((item?.amount - parseInt(item?.discount_amounts?.[0]?.amount || 0)) / 100, 2)}
                                        </span>
                                        <span className="text-sm text-gray-500">/{recurring}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <div className="flex justify-between items-center p-6 rounded-2xl bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border border-indigo-200/50 dark:border-indigo-700/50">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">Total Amount</h3>
                            <div className="flex items-baseline gap-1">
                                <span className="text-lg text-gray-500">$</span>
                                <span className="text-3xl font-extrabold text-gray-800 dark:text-gray-200">
                                    {formatNumber(upcomingInvoice?.total / 100, 2)}
                                </span>
                                <span className="text-lg text-gray-500">/{recurring}</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex justify-end mt-6">
                        <Button 
                            variant="solid" 
                            className="!px-6 !py-3 !rounded-xl"
                            onClick={() => setIsPriceDetailsDialogOpen(false)}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </Dialog>
            {showCancelSubscriptionModal && <CancelSubscriptionModal onCancel={() => setShowCancelSubscriptionModal(false)} />}
        </div>
    )
}

export default MySubscription;